import { useEffect, useState, useCallback, useRef } from "react";
import { Sparkle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import { reconcileRazorpayOrder } from "../lib/razorpayReconcile";
import { useAuth } from "../context/AuthContext";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { inrFull } from "../lib/format";

const DISMISS_KEY = "estima_opportunity_dismissed_until";

export default function AddonOpportunityBanner() {
  const { refresh: refreshUser } = useAuth();
  const { access, refresh: refreshAccess } = useFeatureAccess();
  const [opps, setOpps] = useState([]);
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(false);
  const handlerFiredRef = useRef(false);

  const load = useCallback(async () => {
    if (!access || access.tier !== "basic") return;
    try {
      const { data } = await api.get("/insights/addon-opportunities");
      setOpps(data.opportunities || []);
    } catch {
      setOpps([]);
    }
  }, [access]);

  useEffect(() => {
    try {
      const until = parseInt(sessionStorage.getItem(DISMISS_KEY) || "0");
      if (until && until > Date.now()) setHidden(true);
    } catch { /* ignore */ }
    load();
  }, [load]);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); } catch { /* ignore */ }
    setHidden(true);
  };

  const claim = async (opp, cycle) => {
    setBusy(`${opp.slug}-${cycle}`);
    try {
      const Razorpay = await loadRazorpay();
      const { data: order } = await api.post("/billing/addon/create-order-with-opportunity", {
        addon_slug: opp.slug, cycle, apply_opportunity: true,
      });
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Estima · Add-on (Opportunity)",
        description: order.plan_label,
        order_id: order.order_id,
        prefill: { name: order.customer.name, email: order.customer.email },
        theme: { color: "#C85A32" },
        handler: async (resp) => {
          handlerFiredRef.current = true;
          try {
            await api.post("/billing/addon/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`${opp.name} unlocked at ${opp.discount_pct}% off.`);
            await Promise.all([refreshUser(), refreshAccess(), load()]);
          } catch (e) {
            const r = await reconcileRazorpayOrder("addon", resp.razorpay_order_id);
            if (r.ok) {
              toast.success(`${opp.name} unlocked.`);
              await Promise.all([refreshUser(), refreshAccess(), load()]);
            } else {
              toast.error(formatApiErrorDetail(e.response?.data?.detail));
            }
          } finally { setBusy(null); }
        },
        modal: {
          ondismiss: async () => {
            if (handlerFiredRef.current) { setBusy(null); return; }
            const r = await reconcileRazorpayOrder("addon", order.order_id);
            setBusy(null);
            if (r.ok) {
              toast.success(`Payment received — ${opp.name} unlocked.`);
              await Promise.all([refreshUser(), refreshAccess(), load()]);
            }
          },
        },
      };
      new Razorpay(opts).open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  if (hidden || !access || access.tier !== "basic" || opps.length === 0) return null;

  return (
    <div className="card-flat p-5 mb-8 border-[hsl(var(--secondary))]" data-testid="addon-opportunity-banner">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[hsl(var(--secondary))] mt-1" />
          <div>
            <div className="eyebrow text-[hsl(var(--secondary))] mb-1">Limited offer</div>
            <h3 className="font-serif text-xl mb-1">You've been eyeing these — here's a discount.</h3>
            <p className="text-sm text-muted-foreground">
              Based on your recent activity, we're unlocking these add-ons at a one-time discount.
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
          data-testid="addon-opportunity-dismiss"
        >
          <X size={16} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        {opps.map((o) => (
          <div key={o.slug} className="border hairline p-4" data-testid={`addon-opportunity-${o.slug}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">{o.name}</div>
              <span className="eyebrow text-[hsl(var(--secondary))]">{o.discount_pct}% off</span>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              You viewed this {o.views} time{o.views === 1 ? "" : "s"} recently.
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => claim(o, "monthly")}
                disabled={busy === `${o.slug}-monthly`}
                className="btn-ghost px-3 py-2 text-xs flex-1"
                data-testid={`addon-opp-${o.slug}-buy-monthly`}
              >
                <span className="line-through text-muted-foreground mr-1">{inrFull(o.regular_monthly)}</span>
                <span className="num-metric">{inrFull(o.discounted_monthly)}</span>
                <span className="text-[10px] text-muted-foreground ml-1">/mo</span>
              </button>
              <button
                onClick={() => claim(o, "yearly")}
                disabled={busy === `${o.slug}-yearly`}
                className="btn-primary px-3 py-2 text-xs flex-1"
                data-testid={`addon-opp-${o.slug}-buy-yearly`}
              >
                <span className="num-metric">{inrFull(o.discounted_yearly)}</span>
                <span className="text-[10px] ml-1">/yr</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
